/**
 * Per-unit floating bar rendered as a billboarded THREE.Sprite with a
 * CanvasTexture surface. Always-on-top (depthTest:false, renderOrder:999)
 * per AAA-MOBA convention — losing track of HP behind a wall is bad UX
 * even at our top-down camera angle.
 *
 * Configurable rows: ["hp"] for creatures + buildings, ["hp","mana","stam"]
 * for players. Canvas is redrawn only when row values change (cached
 * last-state); position is updated each frame from the manager.
 *
 * Spec: docs/superpowers/specs/2026-05-03-floating-overhead-ui-design.md §"Architecture" Layer A
 */
import * as THREE from "three";

export type BarRow = "hp" | "mana" | "stam";

export interface BarValues {
  hp?: { cur: number; max: number };
  mana?: { cur: number; max: number };
  stam?: { cur: number; max: number };  // 0..100 (no max)
  level?: number;                        // drawn in the left gutter (players only)
}

export type TeamRelation = "ally" | "enemy" | "neutral";

const CANVAS_W = 128;
const ROW_H = 11;       // HP row (primary resource)
const SUB_ROW_H = 6;    // mana / stamina rows — ~50% of HP, they read as thinner secondaries
const ROW_GAP = 1;
const PADDING = 2;

/** Per-row pixel height: HP is full, mana/stamina are the thinner sub-rows. */
function rowHeight(row: BarRow): number {
  return row === "hp" ? ROW_H : SUB_ROW_H;
}

// Left gutter that holds the level badge (playtest 2026-06-12: "level should be
// to the LEFT of the bars, not above them"). Baked into the same sprite canvas
// as the bars so it stays pinned to their left edge at every zoom level — a DOM
// element can't track a billboarded sprite's varying on-screen width.
const LEVEL_GUTTER_W = 26;
const LEVEL_GUTTER_GAP = 2;

const COLORS = {
  ally:    { hpFrom: "#5bc94a", hpTo: "#338a22" },
  enemy:   { hpFrom: "#f76a6a", hpTo: "#a83838" },
  neutral: { hpFrom: "#cccccc", hpTo: "#777777" },
};

// Level-badge fill per relation — mirrors the .fl-level-badge CSS it replaces.
const LEVEL_BG: Record<TeamRelation, string> = {
  ally: "#2e7eda",
  enemy: "#c84040",
  neutral: "#6a6a6a",
};

const MANA_COLOR = "#5b8fd8";
const STAM_COLOR = "#e09a28";
const BG = "rgba(20, 8, 8, 0.85)";
const BORDER = "rgba(0, 0, 0, 0.9)";

// LoL-style sub-bar ticks: divider every N units of `max` so larger pools
// show more ticks. Fixed-magnitude reference, not relative-percentage.
const TICK_INTERVAL: Record<BarRow, number> = {
  hp: 100,
  mana: 50,
  stam: 25,
};
const TICK_COLOR = "rgba(0, 0, 0, 0.6)";
const TICK_MIN_PX = 3; // collapse to multiples when ticks would be denser than this

export interface SpriteBar {
  sprite: THREE.Sprite;
  /** Update bar values; redraws canvas only if any value changed. */
  update(values: BarValues, relation: TeamRelation): void;
  /** Set world position (manager calls this once per frame). */
  setPosition(x: number, y: number, z: number): void;
  /** Show/hide. */
  setVisible(v: boolean): void;
  /** Free GPU resources. */
  dispose(): void;
}

/**
 * Build a sprite-bar for the given row config.
 *  - rows.length === 1  → HP only (creatures + buildings)
 *  - rows.length === 3  → HP + mana + stamina (players)
 */
export function createSpriteBar(rows: BarRow[], opts?: { levelGutter?: boolean }): SpriteBar {
  if (rows.length < 1) throw new Error("sprite-bar requires at least one row");

  const withLevel = !!opts?.levelGutter;
  const gutterW = withLevel ? LEVEL_GUTTER_W + LEVEL_GUTTER_GAP : 0;
  const rowsTotalH =
    rows.reduce((h, r) => h + rowHeight(r), 0) + Math.max(0, rows.length - 1) * ROW_GAP + PADDING * 2;
  const canvas = document.createElement("canvas");
  canvas.width = gutterW + CANVAS_W;
  canvas.height = rowsTotalH;

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;

  const mat = new THREE.SpriteMaterial({
    map: tex,
    depthTest: false,
    transparent: true,
  });
  const sprite = new THREE.Sprite(mat);
  // Bars region (CANVAS_W px) stays ~2.5u wide regardless of the gutter, so the
  // HP/mana/stam rows render the same size with or without a level badge. The
  // gutter just widens the sprite to the left.
  const worldW = 2.5 * (canvas.width / CANVAS_W);
  const aspect = canvas.height / canvas.width;
  sprite.scale.set(worldW, worldW * aspect, 1);
  sprite.renderOrder = 999;

  // Cache last-rendered values so we only redraw on change
  let lastKey = "";

  function render(values: BarValues, relation: TeamRelation): void {
    const key = `${relation}|L${withLevel ? (values.level ?? "") : ""}|${values.hp?.cur ?? 0}/${values.hp?.max ?? 0}|${values.mana?.cur ?? 0}/${values.mana?.max ?? 0}|${values.stam?.cur ?? 0}/${values.stam?.max ?? 100}`;
    if (key === lastKey) return;
    lastKey = key;

    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Level badge in the left gutter (players only).
    if (withLevel && values.level !== undefined) {
      drawLevelBadge(ctx, values.level, relation);
    }

    // Bars are drawn in a translated frame so drawRow/drawTicks keep using
    // CANVAS_W-relative coordinates unchanged.
    ctx.save();
    ctx.translate(gutterW, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, CANVAS_W, canvas.height);
    ctx.strokeStyle = BORDER;
    ctx.strokeRect(0.5, 0.5, CANVAS_W - 1, canvas.height - 1);

    let y = PADDING;
    for (const row of rows) {
      drawRow(ctx, row, y, values, relation);
      y += rowHeight(row) + ROW_GAP;
    }
    ctx.restore();
    tex.needsUpdate = true;
  }

  function drawLevelBadge(ctx: CanvasRenderingContext2D, level: number, relation: TeamRelation): void {
    const cx = LEVEL_GUTTER_W / 2;
    const cy = canvas.height / 2;
    const rad = Math.min(LEVEL_GUTTER_W, canvas.height) / 2 - 1;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.fillStyle = LEVEL_BG[relation];
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${Math.round(rad * 1.25)}px 'Cinzel', system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(level), cx, cy + 1);
  }

  function drawRow(
    ctx: CanvasRenderingContext2D,
    row: BarRow,
    y: number,
    values: BarValues,
    relation: TeamRelation,
  ): void {
    const h = rowHeight(row);
    const innerW = CANVAS_W - PADDING * 2;
    let pct = 0;
    let max = 0;
    let fillStyle: CanvasGradient | string = "#666";

    if (row === "hp" && values.hp && values.hp.max > 0) {
      max = values.hp.max;
      pct = values.hp.cur / max;
      const colors = COLORS[relation];
      const grad = ctx.createLinearGradient(0, y, 0, y + h);
      grad.addColorStop(0, colors.hpFrom);
      grad.addColorStop(1, colors.hpTo);
      fillStyle = grad;
    } else if (row === "mana" && values.mana && values.mana.max > 0) {
      max = values.mana.max;
      pct = values.mana.cur / max;
      fillStyle = MANA_COLOR;
    } else if (row === "stam" && values.stam) {
      max = values.stam.max ?? 100;
      pct = max > 0 ? values.stam.cur / max : 0;
      fillStyle = STAM_COLOR;
    }
    pct = Math.max(0, Math.min(1, pct));

    // Row background
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(PADDING, y, innerW, h);
    // Fill
    ctx.fillStyle = fillStyle;
    ctx.fillRect(PADDING, y, Math.round(innerW * pct), h);
    // Ticks (drawn after fill so they read on both filled and empty halves)
    drawTicks(ctx, y, innerW, max, TICK_INTERVAL[row], h);
  }

  function drawTicks(
    ctx: CanvasRenderingContext2D,
    y: number,
    innerW: number,
    max: number,
    interval: number,
    h: number,
  ): void {
    if (max <= interval) return;
    const pxPerUnit = innerW / max;
    let stride = interval;
    while (stride * pxPerUnit < TICK_MIN_PX) stride += interval;
    ctx.fillStyle = TICK_COLOR;
    for (let v = stride; v < max; v += stride) {
      const x = PADDING + Math.round(v * pxPerUnit);
      ctx.fillRect(x, y, 1, h);
    }
  }

  return {
    sprite,
    update(values, relation) {
      render(values, relation);
    },
    setPosition(x, y, z) {
      sprite.position.set(x, y, z);
    },
    setVisible(v) {
      sprite.visible = v;
    },
    dispose() {
      tex.dispose();
      mat.dispose();
    },
  };
}
